import React, { useState, useEffect } from 'react'
import { Button, Collapse, Empty, Pagination, Table, Tag, notification } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import RestaurantMenuForm from './RestaurantMenuForm'
import {
  update_selected_menu,
  update_visibility
} from '../slices/restaurantSlice'
import NumberFormat from 'react-number-format'
import {
  _get_menus,
  _get_vendor_restaurant,
  _delete_menu_
} from '../common/axios_services'
import MenuDrawer from './MenuDrawer'
import useCategories from '../hooks/useCategories'

const RestaurantMenuContent = ({
  page,
  per_page,
  user,
  handlePageChange,
  onShowSizeChange,
  updateCount,
  onRestaurantIdChange,
  categories
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const selectedMenu = useSelector(state => state.restaurant.selectedMenu)
  const visible = useSelector(state => state.restaurant.visible)
  const activeMenuTab = useSelector(state => state.restaurant.activeMenuTab)
  const [menus, setMenus] = useState([])
  const [loading, setLoading] = useState(false)
  const [restaurantId, setRestaurantId] = useState(null)
  const [updateCountLocal, setUpdateCountLocal] = useState(updateCount)

  const handleMenuClick = menu => {
    dispatch(update_selected_menu(menu))
    dispatch(update_visibility(true))
  }

  const drawerVisibility = () => {
    dispatch(update_visibility(!visible))
    if (selectedMenu) {
      dispatch(update_selected_menu(null))
    }
  }

  const handleDeleteMenu = async () => {
    if (selectedMenu) {
      try {
        let response
        response = await _delete_menu_(selectedMenu.id)
        openNotificationWithIcon(
          'success',
          response?.data?.title || 'Deleted successfully',
          response?.data?.message || 'Item deleted'
        )
        setUpdateCountLocal(updateCountLocal + 1)
        drawerVisibility()
      } catch (err) {
        openNotificationWithIcon('error', 'Failed to delete')
      }
    }
  }

  const openNotificationWithIcon = (type, title, message) => {
    notification[type]({
      message: title,
      description: message,
      placement: 'bottom-right'
    })
  }

  useEffect(() => {
    const fetchRestaurantId = async () => {
      try {
        const response = await _get_vendor_restaurant()
        if (response?.data?.data?.id) {
          const id = response.data.data.id
          setRestaurantId(id)
          onRestaurantIdChange(id)
        }
      } catch (err) {
        console.error('Failed to fetch restaurant ID:', err)
      }
    }
    fetchRestaurantId()
  }, [onRestaurantIdChange])

  const getMenus = async () => {
    try {
      setLoading(true)
      const get_menus = await _get_menus(user?.business?.id, 'restaurant')
      setMenus(get_menus.data.data)
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    getMenus()
  }, [page, per_page, updateCountLocal, restaurantId])

  const callback = () => {
    getMenus()
  }
  const menuColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text, record) => (
        <div className='flex items-center gap-2'>
          <span className='line-clamp-1'>{text}</span>
          {record.deliveryDateType === 'next_day' ? (
            <Tag color='red'>Next Day</Tag>
          ) : (
            <Tag color='green'>Same Day</Tag>
          )}
        </div>
      )
    },
    {
      title: 'Opening Stock',
      dataIndex: 'openingStock',
      key: 'openingStock'
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (text) => `NGN${parseFloat(text || 0).toFixed(2)}`
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Button type='link' onClick={() => handleMenuClick(record)}>
          Edit
        </Button>
      )
    }
  ]

  return (
    <div className={'mt-10'}>
      {menus.length === 0 ? (
        <Empty description='No menu items available. Create a new menu.'>
          <Button
            type='primary'
            onClick={drawerVisibility}
            className='add-menu-button mx-auto rounded-xl'
            icon={<PlusOutlined />}
          >
            Create Item
          </Button>
        </Empty>
      ) : (
        <>
          <div className='desktop show-all'>
            <Table
              columns={menuColumns}
              dataSource={menus}
              pagination={false}
              rowKey='id'
              scroll={{ x: 'max-content' }}
            />
            <Pagination
              showSizeChanger
              onShowSizeChange={onShowSizeChange}
              defaultCurrent={1}
              total={menus.length}
              current={page}
              pageSize={per_page}
              onChange={handlePageChange}
              className='mt-4'
            />
          </div>
          <div className='mobile topup-data'>
            <Collapse defaultActiveKey={[0]}>
              {menus.map((item, index) => (
                <Collapse.Panel
                  showArrow={false}
                  key={index}
                  header={
                    <div className='flex items-center justify-between gap-2 w-full pr-2'>
                      <p style={{ display: 'inline', margin: 0 }}>
                        {item.name}&nbsp;-&nbsp;
                        <span className='small_currency'>NGN</span>
                        <NumberFormat
                          thousandSeparator={true}
                          prefix={''}
                          className='numeric'
                          displayType={'text'}
                          value={parseFloat(item.price).toFixed(2)}
                        />
                      </p>
                      {item.deliveryDateType === 'next_day' ? (
                        <Tag color='red'>Next Day</Tag>
                      ) : (
                        <Tag color='green'>Same Day</Tag>
                      )}
                    </div>
                  }
                >
                  <div>
                    <ul className='mobile-list-flex'>
                      <li className='flex gap-4'>
                        <span>Name:</span>
                        <span className='text-right'>{item.name}</span>
                      </li>
                      <li className='flex gap-4 items-center'>
                        <span>Delivery Type:</span>
                        <span>
                          {item.deliveryDateType === 'next_day' ? (
                            <Tag color='red'>Next Day</Tag>
                          ) : (
                            <Tag color='green'>Same Day</Tag>
                          )}
                        </span>
                      </li>
                      <li className='flex gap-4'>
                        <span>Opening Stock:</span>
                        <span>{item.openingStock}</span>
                      </li>
                      <li className='flex gap-4'>
                        <span>Sold Stock:</span>
                        <span>{item.soldStock}</span>
                      </li>
                      <li className='flex gap-4'>
                        <span>Available Stock:</span>
                        <span>{item.availableStock}</span>
                      </li>
                      <li className='flex gap-4'>
                        <span>Price:</span>
                        <span>
                          <span className='small_currency'>NGN</span>
                          <NumberFormat
                            thousandSeparator={true}
                            prefix={''}
                            className='numeric'
                            displayType={'text'}
                            value={parseFloat(item.price).toFixed(2)}
                          />
                        </span>
                      </li>
                      <li className='flex gap-4'>
                        <span>Action:</span>
                        <span>
                          <Button
                            type='link'
                            onClick={() => handleMenuClick(item)}
                          >
                            Edit
                          </Button>
                        </span>
                      </li>
                    </ul>
                  </div>
                </Collapse.Panel>
              ))}
            </Collapse>
            <div style={{ marginTop: '2%' }}></div>
            <Pagination
              showSizeChanger
              onShowSizeChange={onShowSizeChange}
              defaultCurrent={1}
              total={menus.length}
              current={page}
              pageSize={per_page}
              onChange={handlePageChange}
              className='mt-4'
            />
          </div>
        </>
      )}

      <MenuDrawer
        visible={activeMenuTab === 'restaurant' ? visible : false}
        title='Manage Restaurant Menu'
        selectedMenu={selectedMenu}
        drawerVisibility={drawerVisibility}
      >
        <RestaurantMenuForm
          selectedMenu={selectedMenu}
          categories={categories}
          drawerVisibility={drawerVisibility}
          updateCount={updateCountLocal}
          user={user}
          callback={callback}
          setUpdateCount={setUpdateCountLocal}
          navigate={navigate}
        />
        {selectedMenu && (
          <div className='mt-4'>
            <button
              type='button'
              onClick={handleDeleteMenu}
              className='menu-update-btn bg-red-500 text-white hover:bg-red-600 border-none px-10 py-3 rounded-xl tracking-wider transition-colors'
            >
              Delete Menu
            </button>
          </div>
        )}
      </MenuDrawer>
    </div>
  )
}

export default RestaurantMenuContent
